import basicAppConfig from "@next-platform/client-common-core/infra/constants/basicAppConfig";
import { Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import { useMemo } from "react";

import { LocalizedLink } from "@/components";
import NavigateBackButton from "@/components/common/NavigateBackButton";
import Trans from "@/components/Trans/Trans";
import { EXTERNAL_ROUTES_FOR_APPLICATION } from "@/constants";

interface IReportHeaderProps {
  itemType: "character" | "lorebook" | "rulebook";
}

type ReportItemType = IReportHeaderProps["itemType"];

const ReportIssueBlock = ({
  itemType,
  complaintPolicyTransComponents,
}: {
  itemType: ReportItemType;
  complaintPolicyTransComponents: Record<string, JSX.Element>;
}) => (
  <div className="flex flex-col gap-sm">
    <Text as="h1" variant={TextVariants.HEADING_4_BOLD} className="m-0">
      {itemType === "character" ? <Trans i18nKey="chatbot:reportPage.issue.title" /> : null}
      {itemType === "lorebook" ? <Trans i18nKey="lorebook:reportPage.issue.title" /> : null}
      {itemType === "rulebook" ? <Trans i18nKey="rulebook:reportPage.issue.title" /> : null}
    </Text>
    <Text variant={TextVariants.PARAGRAPH_MEDIUM_REGULAR}>
      {itemType === "character" ? <Trans i18nKey="chatbot:reportPage.issue.description" /> : null}
      {itemType === "lorebook" ? <Trans i18nKey="lorebook:reportPage.issue.description" /> : null}
      {itemType === "rulebook" ? <Trans i18nKey="rulebook:reportPage.issue.description" /> : null}
    </Text>
    <Text variant={TextVariants.PARAGRAPH_MEDIUM_REGULAR}>
      {itemType === "character" ? <Trans i18nKey="chatbot:reportPage.handlingTime" /> : null}
      {itemType === "lorebook" ? <Trans i18nKey="lorebook:reportPage.handlingTime" /> : null}
      {itemType === "rulebook" ? <Trans i18nKey="rulebook:reportPage.handlingTime" /> : null}
    </Text>
    <Text variant={TextVariants.PARAGRAPH_MEDIUM_REGULAR}>
      {itemType === "character" ? <Trans i18nKey="chatbot:reportPage.complaintPolicyLink" components={complaintPolicyTransComponents} /> : null}
      {itemType === "lorebook" ? <Trans i18nKey="lorebook:reportPage.complaintPolicyLink" components={complaintPolicyTransComponents} /> : null}
      {itemType === "rulebook" ? <Trans i18nKey="rulebook:reportPage.complaintPolicyLink" components={complaintPolicyTransComponents} /> : null}
    </Text>
  </div>
);

const ReportGuidelinesBlock = ({
  itemType,
  transComponentsProps,
}: {
  itemType: ReportItemType;
  transComponentsProps: Record<string, JSX.Element>;
}) => (
  <>
    <div className="flex flex-col gap-sm">
      <Text variant={TextVariants.HEADING_6_BOLD}>
        {itemType === "character" ? <Trans i18nKey="chatbot:reportPage.guidelines.title" /> : null}
        {itemType === "lorebook" ? <Trans i18nKey="lorebook:reportPage.guidelines.title" /> : null}
        {itemType === "rulebook" ? <Trans i18nKey="rulebook:reportPage.guidelines.title" /> : null}
      </Text>
      <Text variant={TextVariants.PARAGRAPH_MEDIUM_REGULAR}>
        {itemType === "character" ? <Trans i18nKey="chatbot:reportPage.web.guidelines.description" /> : null}
        {itemType === "lorebook" ? <Trans i18nKey="lorebook:reportPage.web.guidelines.description" /> : null}
        {itemType === "rulebook" ? <Trans i18nKey="rulebook:reportPage.web.guidelines.description" /> : null}
      </Text>
    </div>
    <Text>
      {itemType === "character" ? <Trans i18nKey="chatbot:reportPage.web.guidelines.consult" components={transComponentsProps} /> : null}
      {itemType === "lorebook" ? <Trans i18nKey="lorebook:reportPage.web.guidelines.consult" components={transComponentsProps} /> : null}
      {itemType === "rulebook" ? <Trans i18nKey="rulebook:reportPage.web.guidelines.consult" components={transComponentsProps} /> : null}
    </Text>
  </>
);

const ReportHeader = ({ itemType }: IReportHeaderProps) => {
  const applicationId = basicAppConfig.applicationId;
  const guidelinesUrl = EXTERNAL_ROUTES_FOR_APPLICATION[applicationId as keyof typeof EXTERNAL_ROUTES_FOR_APPLICATION]?.guidelines;
  const transComponentsProps = useMemo(
    () => ({
      guidelinesLink: <LocalizedLink className="text-link mx-1" to={guidelinesUrl ?? "#"} target="_blank" aria-label="community-guidelines" />,
    }),
    [guidelinesUrl],
  );
  const complaintPolicyTransComponents = useMemo(
    () => ({
      complaintPolicyLink: <LocalizedLink className="text-link" to="/complaint" aria-label="complaint-policy" />,
    }),
    [],
  );

  return (
    <div className="flex flex-col gap-lg">
      <NavigateBackButton />
      <ReportIssueBlock itemType={itemType} complaintPolicyTransComponents={complaintPolicyTransComponents} />
      <ReportGuidelinesBlock itemType={itemType} transComponentsProps={transComponentsProps} />
    </div>
  );
};

export default ReportHeader;
